import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { authAPI, projectConfigAPI } from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Logo from '../components/Logo';
import { useLogoTapFlush } from '../hooks/useLogoTapFlush';
import { beginProductAction, classifyProductFailure } from '../lib/clientAnalytics';

type ViewState = 'login' | 'forgot' | 'otp' | 'newPassword';

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const setProjectConfig = useAuthStore((state) => state.setProjectConfig);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Forgot password flow states
  const [view, setView] = useState<ViewState>('login');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Hidden 10-tap-on-logo cache flush gesture. See hooks/useLogoTapFlush.ts.
  const { onTap: onLogoTap, notice: cacheFlushNotice } = useLogoTapFlush();

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    const attempt = beginProductAction('login', { entry_point: 'login_form' });

    try {
      // Call the external recruitment login API
      const response = await authAPI.login(email, password);
      
      if (response.data.status === 'success' || response.data.token) {
        const payload = response.data;
        const token = payload?.token || payload?.data?.token;
        const user = payload?.user || payload?.data?.user;

        if (!token || !user) {
          attempt.fail('server');
          setError('Login failed: missing token or user in response');
          return;
        }

        // Complete under the login attempt's identity before setAuth changes it.
        // A success-looking envelope without both credentials is never success.
        attempt.succeed();
        setAuth(token, user, { isMock: false });

        // Fetch project config for the user's assigned project (non-blocking)
        const primaryProjectId = user.adminProjectId || user.accessibleProjects?.[0]?.projectId;
        if (primaryProjectId) {
          projectConfigAPI.getPublicConfig(primaryProjectId)
            .then((cfgRes) => {
              if (cfgRes.data?.data) {
                setProjectConfig(cfgRes.data.data);
              }
            })
            .catch((err) => console.warn('Failed to fetch project config:', err));
        }

        navigate('/dashboard');
      } else {
        attempt.fail('business_rule');
        setError(response.data.message || 'Login failed');
      }
    } catch (err: any) {
      const reason = classifyProductFailure(err);
      if (reason === 'cancelled') attempt.cancel('cancelled');
      else attempt.fail(reason);
      // Extract actual error message from API response, fallback to generic message
      const apiMessage = err.response?.data?.message;
      const apiError = err.response?.data?.error;
      const message = apiMessage || apiError || 'Invalid email or password';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await authAPI.forgotPassword(email);
      if (response.data.status === 'success' || response.status === 200) {
        setSuccess('OTP sent to your email address');
        setView('otp');
      } else {
        setError(response.data.message || 'Failed to send OTP');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send OTP';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await authAPI.verifyOTP(email, otp);
      if (response.data.status === 'success' || response.status === 200) {
        setSuccess('OTP verified successfully');
        setView('newPassword');
      } else {
        setError(response.data.message || 'Invalid OTP');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Invalid OTP';
      backToRequestOtpIfAsked(err);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authAPI.updatePassword(email, otp, newPassword);
      if (response.data.status === 'success' || response.status === 200) {
        setSuccess('Password updated successfully! Please login with your new password.');
        resetForgotPasswordFlow();
      } else {
        setError(response.data.message || 'Failed to update password');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to update password';
      backToRequestOtpIfAsked(err);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // The server says when an OTP can no longer be used (expired, used up, or too many
  // wrong tries). Take the person back to "send OTP" rather than leave them on a
  // screen that can only fail; the message says why.
  const backToRequestOtpIfAsked = (err: any) => {
    if (err?.response?.data?.code !== 'request_new_otp') return;
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setView('forgot');
  };

  const resetForgotPasswordFlow = () => {
    setView('login');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      <div className="flex min-h-screen">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-[#2E5EA8] via-[#254F94] to-[#1B3D7A]">
          {/* Subtle overlay pattern */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.12),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(0,0,0,0.08),transparent_50%)]" />
          </div>

          <div className="relative z-10 flex flex-col justify-between w-full px-12 py-10">
            {/* Top - Logo + Name (Logo is the hidden 10-tap cache-flush target) */}
            <div className="flex items-center gap-3">
              <Logo size="sm" onClick={onLogoTap} style={{ cursor: 'default' }} />
              <span className="text-xl font-bold text-white tracking-tight">ChannelPlex</span>
            </div>

            {/* Bottom - Headline + Stats */}
            <div className="mb-8">
              <h1 className="text-[2.75rem] font-bold text-white leading-tight mb-5">
                Workforce Management<br />Made Simple
              </h1>
              <p className="text-base text-white/75 max-w-md leading-relaxed mb-12">
                Track employees, manage deployments, monitor compliance, and streamline your workforce operations in one place.
              </p>

              <div className="flex items-center gap-12">
                <div>
                  <div className="text-3xl font-bold text-white">500+</div>
                  <div className="text-sm text-white/65 mt-0.5">Employees</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">25</div>
                  <div className="text-sm text-white/65 mt-0.5">Clients</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">12</div>
                  <div className="text-sm text-white/65 mt-0.5">Projects</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <p className="text-sm text-white/50">&copy; {new Date().getFullYear()} ChannelPlex. All rights reserved.</p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-[45%] flex items-center justify-center px-6 py-10 bg-gray-50">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-10">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  {view === 'login' && 'Welcome back'}
                  {view === 'forgot' && 'Forgot Password'}
                  {view === 'otp' && 'Verify OTP'}
                  {view === 'newPassword' && 'New Password'}
                </h2>
                <p className="text-sm text-gray-500 mt-1.5">
                  {view === 'login' && 'Sign in to your account to continue'}
                  {view === 'forgot' && 'Enter your email to receive OTP'}
                  {view === 'otp' && 'Enter the OTP sent to your email'}
                  {view === 'newPassword' && 'Create your new password'}
                </p>
              </div>

              {/* Success Message */}
              {success && (
                <div className="mb-5 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-700">
                  {success}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Login Form */}
              {view === 'login' && (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <Input
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@channelplay.in"
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                  />

                  <Button type="submit" className="w-full text-base font-semibold bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 shadow-sm" size="lg" isLoading={isLoading}>
                    Sign In
                  </Button>

                  <div className="text-center">
                    <button
                      type="button"
                      className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      onClick={() => { setView('forgot'); setError(''); setSuccess(''); }}
                    >
                      Forgot Password?
                    </button>
                  </div>

                  <div className="mt-6 rounded-xl bg-primary-50 px-5 py-4">
                    <p className="font-semibold text-gray-700 text-sm mb-0.5">Login Information</p>
                    <p className="text-xs text-gray-500">Use your official email address registered in the system</p>
                  </div>
                </form>
              )}

              {/* Forgot Password Form */}
              {view === 'forgot' && (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <Input
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="Enter your registered email"
                  />

                  <Button type="submit" className="w-full text-base font-semibold bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 shadow-sm" size="lg" isLoading={isLoading}>
                    Send OTP
                  </Button>

                  <button
                    type="button"
                    className="w-full text-sm font-medium text-primary-600 hover:text-primary-700"
                    onClick={resetForgotPasswordFlow}
                  >
                    Back to Login
                  </button>
                </form>
              )}

              {/* OTP Verification Form */}
              {view === 'otp' && (
                <form onSubmit={handleVerifyOTP} className="space-y-5">
                  <Input
                    label="OTP"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                  />

                  <Button type="submit" className="w-full text-base font-semibold bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 shadow-sm" size="lg" isLoading={isLoading}>
                    Verify OTP
                  </Button>

                  <button
                    type="button"
                    className="w-full text-sm font-medium text-primary-600 hover:text-primary-700"
                    onClick={resetForgotPasswordFlow}
                  >
                    Back to Login
                  </button>
                </form>
              )}

              {/* New Password Form */}
              {view === 'newPassword' && (
                <form onSubmit={handleUpdatePassword} className="space-y-5">
                  <Input
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Enter new password"
                  />
                  <Input
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm new password"
                  />

                  <Button type="submit" className="w-full text-base font-semibold bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 shadow-sm" size="lg" isLoading={isLoading}>
                    Update Password
                  </Button>

                  <button
                    type="button"
                    className="w-full text-sm font-medium text-primary-600 hover:text-primary-700"
                    onClick={resetForgotPasswordFlow}
                  >
                    Back to Login
                  </button>
                </form>
              )}
            </div>

            {/* Mobile-only branding (Logo is the hidden 10-tap cache-flush target) */}
            <div className="lg:hidden mt-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Logo size="sm" onClick={onLogoTap} style={{ cursor: 'default' }} />
                <span className="text-lg font-bold text-gray-900">ChannelPlex</span>
              </div>
              <p className="text-xs text-gray-400">
                &copy; {new Date().getFullYear()} ChannelPlex. All rights reserved.
                {' · '}
                <a href="/stats" className="text-gray-400 hover:text-gray-600 underline-offset-2 hover:underline">
                  System Status
                </a>
              </p>
            </div>

            {/* Hidden cache-flush notice — shows for 3s after the 10-tap gesture fires */}
            {cacheFlushNotice && (
              <div
                role="status"
                aria-live="polite"
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-full bg-gray-900/90 px-4 py-2 text-sm text-white shadow-lg"
              >
                {cacheFlushNotice}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
